import { DetectDevicesService } from '@fi-shared/fi-shared-services/detect-devices.service';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    NgForm,
    NgModel,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { ChecklistModel } from '../checklist.model';
import { CheckpointModel } from '../checkpoint.model';
import { FiChecklistCheckpointAddMediaComponent } from '../checkpoint-add-media/checkpoint-add-media.component';
import { AppTextService } from '@fi-shared/app-text/app-text.service';
import { FiMobileAppHelperService } from '@fi-app/utilities/fi-services/mobile-app-helper.service';
import { ChecklistAnswerQrCodeHelper } from '@fi-shared/checklist/checklist-answer-qr-code-helper';
import { FicQRAction } from '../../../../app-cordova/app/fic-services/fic-scan-qr-code.service';
import { FiImageSize } from '@fi-shared/types';
import { ICommentModel } from '@fi-shared/fi-comments/fi-comments';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IFiChecklistModelBase } from '@fi-shared/checklist';

@Component({
    selector: 'fi-checklist-checkpoint-answer',
    templateUrl: './checkpoint-answer.component.html',
    styleUrls: ['./checkpoint-answer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiChecklistCheckpointAnswerComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiChecklistCheckpointAnswerComponent,
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist-checkpoint-answer' },
    standalone: false,
})
export class FiChecklistCheckpointAnswerComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {
    @Input() checklist: IFiChecklistModelBase;
    @Input() checkpoint: CheckpointModel;
    @Input('ngModel') checkpointAnswer;
    @Input() answerDisabled: boolean = false;
    @Input() submitted: boolean = false;
    @Input() attachmentRequired: boolean = false;
    @Input() commentRequired: boolean = false;
    @Input() addFileDisabled: boolean = false;
    @Input() attachFileText: string;
    @Input() hasValidQRCode: boolean = false;
    @Input() qrCodeRequired: boolean;
    @Input() checkpointComments: ICommentModel[] = [];

    @Output() answerChange: EventEmitter<any> = new EventEmitter();
    @Output() onRemoveFile = new EventEmitter();
    @Output() onSelectPhotoMobile = new EventEmitter();
    @Output() onAddPhotoCancel = new EventEmitter();
    @Output() onAddPhotoDone = new EventEmitter();
    @Output() onValidate = new EventEmitter();
    @Output() commentChange = new EventEmitter();
    @Output() attachmentsChange = new EventEmitter();

    @ViewChild('CheckpointAnswerMediaFileForm', { static: false }) CheckpointAnswerMediaFileForm: NgForm;
    @ViewChild(FiChecklistCheckpointAddMediaComponent, { static: false })
    checklistCheckpointAddMediaComponent: FiChecklistCheckpointAddMediaComponent;
    @ViewChild('commentContentModel', { static: false }) commentContentModel: NgModel;
    @ViewChild('checkpointAddMediaModel', { static: false }) checkpointAddMediaModel: NgModel;

    CcmImageSize = FiImageSize;

    possibleAnswers = [
        {
            value: ChecklistModel.Answers.Ok,
            textId: 'ok',
        },
        {
            value: ChecklistModel.Answers.NotOk,
            textId: 'notOk',
        },
    ];
    private qrCodeHelper: ChecklistAnswerQrCodeHelper;

    constructor(
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private mobileAppHelperService: FiMobileAppHelperService,
        private detectDeviceService: DetectDevicesService,
        private customerDataService: FiCustomerDataService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.qrCodeHelper = new ChecklistAnswerQrCodeHelper(
            this.checklist,
            this.checkpoint,
            this.checkpointAnswer,
            this.mobileAppHelperService.mobileAppMode,
            this.customerDataService.customerData.ChecklistPresenceEnabled,
            this.appTextService,
        );
    }

    answerOnChange(event) {
        this.commentContentModel.control.markAsTouched();
        setTimeout(() => {
            this._onValidate();
            this.answerChange.emit(event);
            this._change();
        });
    }

    isCommentDisabled() {
        return this.answerDisabled || !this.checkpointAnswer.Answer || this.qrCodeRequired;
    }

    isAddAttachmentDisabled() {
        return this.addFileDisabled || this.qrCodeRequired;
    }

    canDeleteAttachment() {
        return !this.qrCodeRequired;
    }

    isShowMediaError() {
        return this.submitted && this.CheckpointAnswerMediaFileForm && this.CheckpointAnswerMediaFileForm.invalid;
    }

    isMobileDevice() {
        return this.detectDeviceService.isMobilePhone();
    }

    _onValidate() {
        this.onValidate.emit(this.isValid());
    }

    mediaValidationChange($event) {
        this._onValidate();
    }

    onCommentModelChange() {
        this._onValidate();
        this.commentChange.emit();
        this._change();
    }

    onAttachmentsChanged() {
        this.attachmentsChange.emit();
    }

    isValid() {
        return (
            (!this.commentContentModel || !this.commentContentModel.invalid) &&
            (!this.checkpointAddMediaModel || !this.checkpointAddMediaModel.invalid)
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        this._onValidate();
    }

    writeValue(obj: any) {
        this.checkpointAnswer = obj;
    }

    protected _onChange = (_: any) => {};

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    protected _onTouched = () => {};

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    protected _change() {
        this._onChange(this.checkpointAnswer);
        this._onValidatorChange();
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (this.isValid()) {
            return null;
        }

        const result: ValidationErrors = {};
        if (this.checkpointAddMediaModel.invalid) {
            result.media = true;
        }
        if (this.commentContentModel.invalid) {
            result.comment = true;
        }

        return result;
    }

    protected _onValidatorChange = () => {};

    registerOnValidatorChange?(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validateQRCode(code: FicQRAction) {
        this.qrCodeHelper
            .validateQRCode(code)
            .then(() => {
                this.hasValidQRCode = true;
            })
            .catch((errorData) => {
                this.fiDialogsService.error({
                    messageText: errorData.text,
                    headerText: errorData.title,
                });
            });
    }

    isShowSelectedMedia(): boolean {
        return (
            this.checkpointAnswer.MediaBankFileReferences && this.checkpointAnswer.MediaBankFileReferences.length > 0
        );
    }

    // note: not used

    // _onRemoveFile($event) {
    //     this.onRemoveFile.emit($event);
    //     this._change();
    // }
    // _onSelectPhotoMobile($event) {
    //     this.onSelectPhotoMobile.emit($event);
    // }
    // _onAddPhotoCancel() {
    //     this.onAddPhotoCancel.emit();
    // }
    // _onAddPhotoDone($event) {
    //     this.onAddPhotoDone.emit($event);
    //     this._change();
    // }

    // triggerAddMedia() {
    //     this.checklistCheckpointAddMediaComponent.triggerAddMedia();
    // }
    onChecklistAnswersChanged() {
        this.cdr.detectChanges();
    }
}
